import React, { useRef, useState } from 'react';
import { Plus, Trash2, GripVertical, Move, Eye, EyeOff, Hash } from 'lucide-react';

const VIEW_W = 600;
const VIEW_H = 700;

const SHAPE_OPTIONS = [
  { value: 'circle', label: 'Dairə' },
  { value: 'square', label: 'Kvadrat' },
  { value: 'star', label: 'Ulduz' },
  { value: 'hexagon', label: 'Altıbucaq' },
  { value: 'diamond', label: 'Almaz' },
  { value: 'triangle', label: 'Üçbucaq' },
  { value: 'heart', label: 'Ürək' },
  { value: 'door', label: 'Qapı' },
  { value: 'arch', label: 'Arka qapı (cənnət)' },
  { value: 'portal', label: 'İşıq portalı' },
  { value: 'columns', label: 'Sütunlar + arka' },
];

const COLOR_PRESETS = ['#a855f7', '#0ea5e9', '#f97316', '#22c55e', '#eab308', '#ef4444', '#ec4899', '#06b6d4'];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomNodesEditor = ({ customNodes, backgroundLayers, hiddenSpheres, hiddenValues, onChange, onHiddenSpheresChange, onHiddenValuesChange }) => {
  const items = customNodes || [];
  const hidden = new Set(hiddenSpheres || []);
  const hiddenVals = new Set(hiddenValues || []);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [dragging, setDragging] = useState(null);
  const wrapRef = useRef(null);

  const toggleHidden = (id) => {
    if (!onHiddenSpheresChange) return;
    const next = new Set(hidden);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onHiddenSpheresChange(Array.from(next));
  };

  const toggleHiddenValue = (id) => {
    if (!onHiddenValuesChange) return;
    const next = new Set(hiddenVals);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onHiddenValuesChange(Array.from(next));
  };

  const addNode = () => {
    const id = `cn_${Date.now().toString(36)}`;
    const newNode = {
      id,
      label: 'X',
      formula: 'A+B',
      position: { x: 300, y: 350 },
      color: COLOR_PRESETS[items.length % COLOR_PRESETS.length],
      shape: 'circle',
      size: null,
      reduce: true,
      show_pair: true,
    };
    onChange([...items, newNode]);
  };

  const updateNode = (idx, patch) => {
    const copy = [...items];
    copy[idx] = { ...copy[idx], ...patch };
    onChange(copy);
  };

  const deleteNode = () => {
    if (pendingDelete === null) return;
    onChange(items.filter((_, i) => i !== pendingDelete));
    setPendingDelete(null);
  };

  const onPointerDown = (idx) => (e) => {
    e.preventDefault();
    setDragging(idx);
  };
  const onPointerMove = (e) => {
    if (dragging === null || !wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const x = Math.max(15, Math.min(VIEW_W - 15, ((e.clientX - rect.left) / rect.width) * VIEW_W));
    const y = Math.max(15, Math.min(VIEW_H - 15, ((e.clientY - rect.top) / rect.height) * VIEW_H));
    updateNode(dragging, { position: { x: Math.round(x), y: Math.round(y) } });
  };
  const onPointerUp = () => setDragging(null);

  return (
    <div className="admin-section">
      <h3 className="admin-section-title">Hesablanan dairlər</h3>
      <p className="admin-section-desc">
        Mövcud dəyərləri toplayıb yeni dairlər yarada bilərsiniz. Misal: <code className="text-purple-300">Man+B</code> → Manipura + B düyümün cəmi.
        Açarlar: <span className="text-yellow-300/80 text-xs">A, A1, B, B1, V, V1, G, G1, D, D1, E, E1, Zh, Zh1, Z, Z1, I, I1, K, K1, L, L1, M, M1, HRT, HRT1, Man, Man1</span>.
        <br />
        <span className="text-emerald-300/90 text-xs">📌 Yeni: Yaratdığınız dairənin adını (məs. <code>Kalp</code>) sonrakı formulalarda istifadə edə bilərsiniz — əsas hərflər kimi. Adlar həmişə ingilis hərfləri ilə başlamalıdır (A-Z, a-z), nöqtə/boşluq olmasın.</span>
        <br />
        <span className="text-yellow-300/90 text-xs">Σ İl Enerji Uyumu: formulada <code>d1_HRT</code>, <code>d2_HRT</code>, <code>d1_A</code>, <code>d2_A</code> kimi tokenlərlə iki tarixin dəyərlərini birbaşa toplaya bilərsiniz (məs. <code>d1_HRT+d2_HRT</code>).</span>
        <br />
        Operatorlar: + − × ÷ ( ) abs()
      </p>

      <button type="button" onClick={addNode} className="admin-btn-primary mb-3" data-testid="custom-node-add">
        <Plus size={14} /> Yeni dairə əlavə et
      </button>

      {/* List of custom nodes */}
      <div className="flex flex-col gap-3 mb-4">
        {items.length === 0 && (
          <p className="text-white/40 text-sm">Hələ heç bir hesablanan dairə yoxdur.</p>
        )}
        {items.map((node, idx) => (
          <div key={node.id} className="p-3 rounded border border-white/10 bg-black/30" data-testid={`custom-node-row-${node.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full flex-shrink-0" style={{ background: node.color, boxShadow: `0 0 8px ${node.color}99` }} />
              <input
                className="karma-input text-left"
                style={{ maxWidth: 80, fontFamily: 'monospace' }}
                value={node.label}
                onChange={(e) => updateNode(idx, { label: e.target.value })}
                placeholder="Hərf"
                maxLength={6}
                data-testid={`custom-node-label-${node.id}`}
              />
              <input
                className="karma-input flex-1 text-left"
                style={{ fontFamily: 'monospace' }}
                value={node.formula}
                onChange={(e) => updateNode(idx, { formula: e.target.value })}
                placeholder="Formula (məs: Man+Man+A, Kalp+B, abs(Ahs-V))"
                data-testid={`custom-node-formula-${node.id}`}
              />
              <button
                type="button"
                onClick={() => toggleHidden(node.id)}
                className="admin-btn-secondary"
                title={hidden.has(node.id) ? 'Fiquru göstər' : 'Fiquru gizlət'}
                data-testid={`custom-node-visibility-${node.id}`}
                style={hidden.has(node.id) ? { opacity: 0.55 } : undefined}
              >
                {hidden.has(node.id) ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
              <button
                type="button"
                onClick={() => toggleHiddenValue(node.id)}
                className="admin-btn-secondary"
                title={hiddenVals.has(node.id) ? 'Dəyəri göstər' : 'Dəyəri gizlət'}
                data-testid={`custom-node-value-${node.id}`}
                style={hiddenVals.has(node.id) ? { opacity: 0.55, borderColor: 'rgba(248, 113, 113, 0.4)' } : undefined}
              >
                <Hash size={14} />
              </button>
              <button
                type="button"
                onClick={() => setPendingDelete(idx)}
                className="admin-btn-secondary"
                title="Sil"
                data-testid={`custom-node-delete-${node.id}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <select
                className="karma-input"
                value={node.shape || 'circle'}
                onChange={(e) => updateNode(idx, { shape: e.target.value })}
              >
                {SHAPE_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <div className="flex items-center gap-1">
                <input
                  type="color"
                  value={node.color}
                  onChange={(e) => updateNode(idx, { color: e.target.value })}
                  className="h-8 w-12 rounded border border-white/10"
                  title="Fiqur rəngi"
                />
                <input
                  type="color"
                  value={node.text_color || '#ffffff'}
                  onChange={(e) => updateNode(idx, { text_color: e.target.value })}
                  className="h-8 w-10 rounded border border-dashed border-purple-400/60"
                  title="Yazı rəngi (hərf/dəyər)"
                  data-testid={`custom-node-text-color-${node.id}`}
                />
                <input
                  type="range" min="0" max="80" step="2"
                  value={node.size || 0}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    updateNode(idx, { size: v === 0 ? null : v });
                  }}
                  title={`Ölçü: ${node.size || 'default'}`}
                  className="flex-1"
                />
              </div>
              <label className="flex items-center gap-2 text-xs text-white/70">
                <input
                  type="checkbox"
                  checked={node.reduce !== false}
                  onChange={(e) => updateNode(idx, { reduce: e.target.checked })}
                />
                <span>22-yə qədər azalt</span>
              </label>
              <label className="flex items-center gap-2 text-xs text-white/70">
                <input
                  type="checkbox"
                  checked={node.show_pair !== false}
                  onChange={(e) => updateNode(idx, { show_pair: e.target.checked })}
                  data-testid={`custom-node-pair-${node.id}`}
                />
                <span>İkinci dəyər (rəqəm cəmi)</span>
              </label>
              <div className="flex items-center gap-1 col-span-2 md:col-span-4 mt-1">
                <span className="text-xs text-white/60 whitespace-nowrap">📅 Tarix:</span>
                <select
                  className="karma-input"
                  value={node.date_source || 'primary'}
                  onChange={(e) => updateNode(idx, { date_source: e.target.value })}
                  data-testid={`custom-node-date-source-${node.id}`}
                  style={{ flex: '0 0 auto', minWidth: 150 }}
                >
                  <option value="primary">1-ci tarix</option>
                  <option value="secondary">2-ci tarix (uyum)</option>
                  <option value="both">Hər ikisi</option>
                  <option value="sum">Σ Cəm (hər iki blokda göstər, formulada d1_/d2_ istifadə et)</option>
                </select>
                <input
                  className="karma-input"
                  value={node.group_title || ''}
                  onChange={(e) => updateNode(idx, { group_title: e.target.value })}
                  placeholder="Başlıq (opsional, məs: 1-ci tarix üçün dairələr)"
                  maxLength={80}
                  data-testid={`custom-node-group-title-${node.id}`}
                  style={{ flex: 1 }}
                />
              </div>
              <div className="text-xs text-white/40 flex items-center gap-1">
                <Move size={12} /> ({Math.round(node.position?.x || 0)}, {Math.round(node.position?.y || 0)})
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Position canvas */}
      {items.length > 0 && (
        <>
          <h4 className="text-sm font-semibold text-white/80 mb-2">Yerləri sürükləyin</h4>
          <div
            ref={wrapRef}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            style={{
              position: 'relative',
              aspectRatio: `${VIEW_W} / ${VIEW_H}`,
              background: 'rgba(0,0,0,0.4)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8,
              touchAction: 'none',
              userSelect: 'none',
            }}
          >
            <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} style={{ width: '100%', height: '100%' }}>
              {(backgroundLayers || []).map((layer, i) => {
                const fit = layer.fit_mode || 'cover';
                let par = 'xMidYMid slice';
                if (fit === 'contain' || fit === 'fit') par = 'xMidYMid meet';
                else if (fit === 'stretch' || fit === 'fill') par = 'none';
                else if (fit === 'natural' || fit === 'none') par = 'xMidYMid meet';
                return (
                  <g key={`cnbg-${i}`}
                     transform={`translate(${layer.offset_x || 0}, ${layer.offset_y || 0}) translate(${VIEW_W / 2}, ${VIEW_H / 2}) scale(${layer.scale || 1}) translate(${-VIEW_W / 2}, ${-VIEW_H / 2})`}
                     opacity={(layer.opacity ?? 1) * 0.55}>
                    <image
                      href={`${BACKEND_URL}/api/uploads/${layer.filename}`}
                      x="0" y="0" width={VIEW_W} height={VIEW_H}
                      preserveAspectRatio={par}
                    />
                  </g>
                );
              })}
              <g opacity="0.06">
                {Array.from({ length: 13 }).map((_, i) => (
                  <line key={`v${i}`} x1={i * 50} y1="0" x2={i * 50} y2={VIEW_H} stroke="white" strokeWidth="1" />
                ))}
                {Array.from({ length: 15 }).map((_, i) => (
                  <line key={`h${i}`} x1="0" y1={i * 50} x2={VIEW_W} y2={i * 50} stroke="white" strokeWidth="1" />
                ))}
              </g>
              {items.map((n, idx) => (
                <g key={`pv-${n.id}`}
                   style={{ cursor: 'grab' }}
                   onPointerDown={onPointerDown(idx)}
                   data-testid={`custom-node-drag-${n.id}`}>
                  <circle cx={n.position?.x || 300} cy={n.position?.y || 350}
                          r={n.size || 24}
                          fill={n.color}
                          stroke="white" strokeWidth="1" opacity="0.92" />
                  <text x={n.position?.x || 300} y={n.position?.y || 350}
                        textAnchor="middle" dominantBaseline="central"
                        fill="white" fontSize={Math.max(10, (n.size || 24) * 0.6)} fontWeight="700">
                    {n.label || '?'}
                  </text>
                </g>
              ))}
            </svg>
          </div>
        </>
      )}

      {pendingDelete !== null && items[pendingDelete] && (
        <div className="mt-4 p-3 rounded border border-red-500/40 bg-red-500/10 flex items-center justify-between gap-3 flex-wrap"
             data-testid="custom-node-delete-confirm-banner">
          <span className="text-sm text-red-200">
            "<strong>{items[pendingDelete].label}</strong>" dairəsini silmək istəyirsiniz?
          </span>
          <div className="flex gap-2">
            <button type="button" className="admin-btn-secondary" onClick={() => setPendingDelete(null)}>Ləğv et</button>
            <button type="button" className="admin-btn-primary"
                    style={{ background: 'rgba(239,68,68,0.85)', borderColor: '#ef4444' }}
                    onClick={deleteNode}
                    data-testid="custom-node-delete-confirm">Sil</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomNodesEditor;
